import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { currentMonthIST, monthRangeIST, todayIST } from "../../lib/datetime";

/**
 * Planned / As planned / Unplanned / Missed per officer for a date range
 * (§2 Session 4). THE ONE COMPONENT for these four numbers: the Sales
 * Manager's team view and the Director's all-visits view both mount it, so
 * the same officer cannot read differently on the two screens.
 *
 * Every number comes from `/doctor-visits/deviation-counts`, which sums the
 * same `day_progress_for` detector behind the plan's day card — nothing is
 * derived here. Zeros are rendered as zeros. A failed fetch renders the
 * failure, never an empty table: "no deviation" and "could not be read" are
 * different facts.
 *
 * "Missed" is a COUNT OF A GAP — a planned doctor with no visit logged that
 * day. The app knows no visit was logged, not that the officer did not go,
 * and the caption says so on the surface where a manager reads it.
 */
const COLUMNS = [
  ["planned", "Planned"],
  ["as_planned", "As planned"],
  ["unplanned", "Unplanned"],
  ["missed", "Missed"],
];

export default function DeviationCounts({ from, to, soId, testIdPrefix = "deviation" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // With no dates chosen the range is the company's month to date (IST) —
  // the same window the per-officer summary beside it uses.
  const { year, month } = currentMonthIST();
  const dateFrom = from || monthRangeIST(year, month).first;
  const dateTo = to || todayIST();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const params = { date_from: dateFrom, date_to: dateTo };
    if (soId) params.so_id = soId;
    api
      .get("/doctor-visits/deviation-counts", { params })
      .then(({ data: d }) => !cancelled && setData(d))
      .catch((e) => {
        if (cancelled) return;
        setData(null);
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, soId]);

  return (
    <div className="bg-white border border-callisto rounded-xl p-3 md:p-4" data-testid={`${testIdPrefix}-counts`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <p className="text-sm font-semibold text-callisto-navy">Plan deviation</p>
        <p className="text-[11px] text-callisto-muted" data-testid={`${testIdPrefix}-range`}>
          {dateFrom} → {dateTo}
        </p>
      </div>
      <p className="mt-0.5 text-[11px] text-callisto-muted">
        Missed = a doctor on the approved plan for a day with no visit logged that day. The app
        knows no visit was logged, not whether the officer went.
      </p>
      {error ? (
        <p className="mt-3 text-xs text-red-700" data-testid={`${testIdPrefix}-error`}>
          Deviation counts could not be read: {error}
        </p>
      ) : loading && !data ? (
        <p className="mt-3 text-xs text-callisto-muted">Loading…</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm" data-testid={`${testIdPrefix}-table`}>
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-callisto-muted">
                <th className="text-left font-semibold py-1 pr-3">Officer</th>
                {COLUMNS.map(([key, label]) => (
                  <th key={key} className="text-right font-semibold py-1 px-2">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.officers || []).length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-2 text-xs text-callisto-muted">
                    No officers in scope.
                  </td>
                </tr>
              ) : (
                data.officers.map((o) => (
                  <tr
                    key={o.sales_officer_id}
                    className="border-t border-callisto"
                    data-testid={`${testIdPrefix}-row-${o.sales_officer_id}`}
                  >
                    <td className="py-1.5 pr-3 font-semibold text-callisto-navy truncate">{o.name}</td>
                    {COLUMNS.map(([key]) => (
                      <td
                        key={key}
                        className={`py-1.5 px-2 text-right tabular-nums ${
                          key === "missed" && o.counts[key] > 0 ? "text-red-700 font-semibold" : "text-callisto-navy"
                        }`}
                        data-testid={`${testIdPrefix}-${key}-${o.sales_officer_id}`}
                      >
                        {o.counts[key]}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
